import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { useNavigate } from '@tanstack/react-router'
import { adminService, Language } from '../services/adminService'
import { LanguageList } from '../components/LanguageList'
import { Button } from '../components/ui/Button'
import { useTranslation } from '../i18n/hooks/useTranslation'

export function LanguagesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [togglingLanguageId, setTogglingLanguageId] = useState<string | null>(null)

  const queryClient = useQueryClient()

  const { data: languages = [], isLoading, error } = useQuery({
    queryKey: ['all-languages'],
    queryFn: () => adminService.getAllLanguages(),
  })



  const toggleStatusMutation = useMutation({
    mutationFn: (id: string) => adminService.toggleLanguageStatus(id),
    onSuccess: () => {
      // Invalidate both language queries to ensure all components refresh
      queryClient.invalidateQueries({ queryKey: ['languages'] })
      queryClient.invalidateQueries({ queryKey: ['all-languages'] })
      toast.success(t('messages.languageStatusUpdated'))
      setTogglingLanguageId(null)
    },
    onError: () => {
      toast.error(t('messages.languageStatusUpdateFailed'))
      setTogglingLanguageId(null)
    },
  })

  const seedMutation = useMutation({
    mutationFn: () => adminService.seedDefaultLanguages(),
    onSuccess: () => {
      // Invalidate both language queries to ensure all components refresh
      queryClient.invalidateQueries({ queryKey: ['languages'] })
      queryClient.invalidateQueries({ queryKey: ['all-languages'] })
      toast.success(t('messages.defaultLanguagesSeeded'))
    },
    onError: () => {
      toast.error(t('messages.defaultLanguagesSeedFailed'))
    },
  })

  const handleToggleStatus = (language: Language) => {
    setTogglingLanguageId(language.id)
    toggleStatusMutation.mutate(language.id)
  }

  const handleEdit = (language: Language) => {
    navigate({ to: `/languages/${language.code}` })
  }

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('messages.loadingLanguages')}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      {/* Enhanced Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
              {t('languages.title')}
            </h1>
            <p className="text-gray-600 mt-2 text-sm lg:text-base leading-relaxed">
              {t('languages.subtitle')}
            </p>
            {error && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm text-red-600 font-medium">
                    {t('errors.loadingLanguages')}: {error.message}
                  </span>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            {languages.length === 0 && (
              <button
                onClick={() => seedMutation.mutate()}
                disabled={seedMutation.isPending}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {t('buttons.seedDefaults')}
              </button>
            )}
            <button
              onClick={() => navigate({ to: '/languages/create' })}
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 hover:border-blue-700 transition-all duration-200 font-medium text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t('buttons.addLanguage')}
            </button>
          </div>
        </div>
      </div>


      {languages.length === 0 ? (
        <div className="text-center py-12">
          <div className="max-w-md mx-auto">
            <div className="text-6xl mb-4">🌐</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('messages.noLanguagesFound')}
            </h3>
            <p className="text-gray-500 mb-6">
              {t('messages.addFirstLanguage')}
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate({ to: '/languages/create' })}
                variant="primary"
                className="w-full"
              >
                {t('buttons.addLanguage')}
              </Button>
              <Button
                onClick={() => seedMutation.mutate()}
                variant="secondary"
                loading={seedMutation.isPending}
                className="w-full"
              >
                {t('buttons.seedDefaultLanguages')}
              </Button>
              <Button
                onClick={() => {
                  console.log('Opening browser console for manual debugging...')
                  console.log('Run: adminService.getAllLanguages()')
                }}
                variant="tertiary"
                className="w-full"
              >
{t('actions.debugConsole')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <LanguageList
          languages={languages}
          onToggleStatus={handleToggleStatus}
          onEdit={handleEdit}
          togglingLanguageId={togglingLanguageId}
        />
      )}
    </div>
  )
} 